import { Form, Formik, useField } from "formik";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { Client } from "@/features/clients/types";
import {
  FILE_AS_OPTIONS,
  type FileAs,
  FORMS_433_OIC,
  type FormSubmission,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";

interface AttachmentData {
  for_who: FileAs;
}

function toInitialValues(data: Record<string, unknown>): AttachmentData {
  const d = data as Partial<AttachmentData>;
  return { for_who: d.for_who ?? "T" };
}

function ForWhoField({ married }: { married: boolean }) {
  const [field, , helpers] = useField<FileAs>("for_who");
  if (!married) return <p className="text-muted-foreground text-sm">Taxpayer</p>;
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as FileAs)}
    >
      {FILE_AS_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`fw-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`fw-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Fill page for the attachment-style 433 forms (433-A/B/F and OIC variants):
 * these collect no field entry — the entire financial picture is read from the
 * worksheet, transcript and client layers at fill time. The page shows what
 * feeds the PDF plus the filer selector (and, for OIC, the offer-plan months),
 * then Preview / Save-to-Documents.
 */
export function Form433AttachmentEditor({ client, submission, onSubmissionChange }: Props) {
  const married = client.maritalStatus === "MARRIED";
  const isOic = FORMS_433_OIC.has(submission.formNumber);

  return (
    <Formik initialValues={toInitialValues(submission.data)} onSubmit={() => {}}>
      <Form className="flex flex-col gap-6">
        <Card className="gap-4 p-6">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-foreground font-semibold">Prepare for</span>
            <ForWhoField married={married} />
          </div>
          {isOic && (
            <p className="text-muted-foreground text-sm">
              The offer plan (12 or 24 months) and offer amount come from the client's
              financial worksheet <span className="font-medium">Offer</span> tab — set them
              there so the computed values stay consistent across forms.
            </p>
          )}
        </Card>

        <Card className="gap-3 p-6">
          <h3 className="text-foreground text-lg font-bold">Source data</h3>
          <p className="text-muted-foreground text-sm">
            This statement is generated from the client's financial worksheet
            (income, expenses, assets and{" "}
            {isOic ? "quick-sale valuation + future income" : "reasonable collection potential"}
            ), transcript tax periods, and client profile — nothing is re-entered here.
            {isOic
              ? " The offer amount, quick-sale asset total and future-income multiplier are computed from the worksheet."
              : " IRS-allowed expense standards are printed beside the client's actuals."}{" "}
            Use <span className="font-medium">Preview PDF</span> to review, then{" "}
            <span className="font-medium">Save to Documents</span> to file it. Long lists
            overflow onto appended attachment pages.
          </p>
        </Card>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
